import React, { useContext, useEffect, useState } from 'react';
import ProductImage from '../../../../../common/ProductImage';
import { Button, FormControl } from 'react-bootstrap';
import { faMinusCircle, faPlusCircle } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { useAlert } from 'react-alert';
import AuthContext from '../../../../../authentication/AuthContext';
import OrderContext from '../../../OrderContext';
import { IconWrapper } from '../../../../../common/IconWrapper';
import { faTimesCircle } from '@fortawesome/free-solid-svg-icons/faTimesCircle';
import UserContext from '../../../../../user/UserContext';

export default function CarouselProductCard({
    product,
    refreshCarouselInfo,
    upsoldFrom,
    removeProductFromCarousel
}) {
    const [quantity, setQuantity] = useState(product.orderedQuantity);
    const [highlightQuantity, setHighlightQuantity] = useState(false);
    const alert = useAlert();
    const cas = useContext(AuthContext);
    const { refreshOrderInfo, orderId } = useContext(OrderContext);
    const { userDetails } = useContext(UserContext);

    function restrictProductAdvert() {
        axios
            .post(
                window.CONFIG['ws.activoc-web-order-api.suggested-products-supress']
                    .replace('{franchiseCode}', userDetails.currentFranchise.code)
                    .replace('{productCode}', product.productCode),
                {},
                {
                    headers: {
                        Authorization: cas,
                        'Content-Type': 'application/json'
                    }
                }
            )
            .catch(() => alert.error('Unable to remove product'));
        removeProductFromCarousel(product.productCode);
    }

    useEffect(() => {
        setQuantity(product.orderedQuantity);
    }, [product]);

    let decreaseQuantity = () => {
        setHighlightQuantity(true);
        setQuantity(quantity - 1);
        axios
            .post(
                window.CONFIG['ws.activoc-web-order-api.order.lines.quantity.decrease']
                    .replace('{id}', orderId)
                    .replace('{lineId}', product.orderLineId),
                {},
                {
                    headers: {
                        Authorization: cas,
                        'Content-Type': 'application/json'
                    }
                }
            )
            .then(() => {
                refreshOrderInfo();
                setHighlightQuantity(false);
                refreshCarouselInfo();
            })
            .catch(() => alert.error('Unable to decrease product quantity'));
    };

    let increaseQuantity = () => {
        if (product.orderLineId) {
            setHighlightQuantity(true);
            setQuantity(quantity + 1);
            axios
                .post(
                    window.CONFIG['ws.activoc-web-order-api.order.lines.quantity.increase']
                        .replace('{id}', orderId)
                        .replace('{lineId}', product.orderLineId),
                    {},
                    {
                        headers: {
                            Authorization: cas,
                            'Content-Type': 'application/json'
                        }
                    }
                )
                .then(() => {
                    refreshOrderInfo();
                    setHighlightQuantity(false);
                    refreshCarouselInfo();
                })
                .catch(() => {
                    alert.error('Unable to increase product quantity');
                });
        } else {
            setHighlightQuantity(true);
            setQuantity(quantity + 1);
            axios
                .put(
                    window.CONFIG['ws.activoc-web-order-api.order.lines.add'].replace('{orderId}', orderId),
                    { id: orderId, quantity: 1, productCode: product.productCode, upsoldFrom: upsoldFrom },
                    {
                        headers: {
                            Authorization: cas,
                            'Content-Type': 'application/json'
                        }
                    }
                )
                .then(() => {
                    refreshOrderInfo();
                    setHighlightQuantity(false);
                    refreshCarouselInfo();
                })
                .catch(() => {
                    alert.error('Unable to add product line');
                });
        }
    };

    let updateQuantity = () => {
        if (product.orderLineId) {
            setHighlightQuantity(true);
            axios
                .put(
                    window.CONFIG['ws.activoc-web-order-api.order.lines.quantity.set']
                        .replace('{id}', orderId)
                        .replace('{lineId}', product.orderLineId),
                    { id: orderId, quantity: quantity },
                    {
                        headers: {
                            Authorization: cas,
                            'Content-Type': 'application/json'
                        }
                    }
                )
                .then(() => {
                    refreshOrderInfo();
                    setHighlightQuantity(false);
                    refreshCarouselInfo();
                })
                .catch(() => alert.error('Unable to update product quantity'));
        } else {
            if (quantity > 0) {
                setHighlightQuantity(true);
                axios
                    .put(
                        window.CONFIG['ws.activoc-web-order-api.order.lines.add'].replace(
                            '{orderId}',
                            orderId
                        ),
                        {
                            id: orderId,
                            quantity: quantity,
                            productCode: product.productCode,
                            upsoldFrom: upsoldFrom
                        },
                        {
                            headers: {
                                Authorization: cas,
                                'Content-Type': 'application/json'
                            }
                        }
                    )
                    .then(() => {
                        refreshOrderInfo();
                        setHighlightQuantity(false);
                        refreshCarouselInfo();
                    })
                    .catch(() => alert.error('Unable to add product line'));
            }
        }
    };

    return (
        <>
            <ProductImage src={product.image} alt={product.productDescription} />
            <p className={'bold-data description'}>{product.productDescription.toUpperCase()}</p>
            <p>{product.unitSize}</p>
            <p className={'bold-data'}>
                Size <span className={'green-data'}>{product.caseSize}</span> /{' '}
                <span className={'green-data'}>{product.margin}%</span> / RSP{' '}
                <span className={'green-data'}>£{product.rsp.toFixed(2)}</span>
            </p>
            <p className={'bold-data'}>
                <span className={'green-data'}>£{product.cost.toFixed(2)}</span>
            </p>
            {product.editable ? (
                <div className={'quantity-selector flex'}>
                    <Button
                        id={'qty-decrease'}
                        onClick={decreaseQuantity}
                        data-testid={'decrease'}
                        tabIndex={-1}
                    >
                        <IconWrapper icon={faMinusCircle} />
                    </Button>
                    <FormControl
                        type={'text'}
                        maxLength={'3'}
                        size={'3'}
                        value={quantity}
                        onChange={event => {
                            let newQuantity = event.target.value;
                            if (newQuantity === '' || isNaN(newQuantity)) {
                                newQuantity = 0;
                            }
                            if (newQuantity.length > 1 && newQuantity.startsWith('0')) {
                                newQuantity = newQuantity.substring(1, newQuantity.length);
                            }
                            setQuantity(newQuantity);
                        }}
                        onBlur={updateQuantity}
                        className={highlightQuantity && 'quantity-highlight'}
                    />
                    <Button
                        id={'qty-increase'}
                        onClick={increaseQuantity}
                        data-testid={'increase'}
                        tabIndex={-1}
                    >
                        <IconWrapper icon={faPlusCircle} />
                    </Button>
                </div>
            ) : (
                <p>{product.orderedQuantity}</p>
            )}
            <button onClick={restrictProductAdvert} className={'close-icon'} data-testid={'close-icon'}>
                <IconWrapper icon={faTimesCircle} />
            </button>
        </>
    );
}
